import { useEffect, useState } from "react";
import { api, formatApiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Building2, Plus, Loader2, Users, ClipboardList, PauseCircle, PlayCircle, Trash2, Mail, KeyRound, Copy, Check, Phone, MessageCircle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { passwordError, PASSWORD_HINT } from "@/lib/password";
import { WelcomeResultDialog, inviteText } from "@/components/WelcomeResultDialog";
import { EditShopDialog } from "@/components/EditShopDialog";
import { AiUsageAdmin, AssistantUsageAdmin } from "@/components/AiUsageAdmin";
import { PlateLookupAdmin } from "@/components/PlateLookupAdmin";
import { BillingAdmin } from "@/components/BillingAdmin";
import { BusinessDashboard } from "@/components/admin/BusinessDashboard";
import { TicketsAdmin } from "@/components/admin/TicketsAdmin";
import { AddonsAdmin } from "@/components/admin/AddonsAdmin";
import { PlansAdmin } from "@/components/admin/PlansAdmin";
import { HelpAdmin } from "@/components/admin/HelpAdmin";
import { NoticesAdmin } from "@/components/admin/NoticesAdmin";
import { ShopProfileFields, emptyProfile, profilePayload } from "@/components/admin/ShopProfileFields";
import { StatusPill, LastAccess, ShopAdminActions } from "@/components/admin/ShopAdminActions";

const empty = { name: "", owner_name: "", owner_email: "" };

export default function SuperAdminDashboard() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [resetShop, setResetShop] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [createdShop, setCreatedShop] = useState(null);
  const [ownerInvite, setOwnerInvite] = useState(null);
  const resendOwnerInvite = async (s) => {
    try { const { data } = await api.post(`/shops/${s.id}/owner-invite`); setOwnerInvite({ ...data, shop_name: s.name }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const [editShop, setEditShop] = useState(null);
  const [meta, setMeta] = useState({});
  const [newProfile, setNewProfile] = useState(emptyProfile);
  const loadMeta = () => api.get("/admin/shops/meta").then(({ data }) => setMeta(data)).catch(() => {});
  const onMeta = (id, m) => setMeta((p) => ({ ...p, [id]: m }));

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/shops");
      setShops(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadMeta(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/shops", form);
      try { const { data: m } = await api.put(`/admin/shops/${data.id}/profile`, profilePayload(newProfile)); onMeta(data.id, m); }
      catch (err) { toast.error(`Oficina criada, mas o cadastro completo não salvou: ${formatApiError(err)}`); }
      setNewProfile(emptyProfile);
      setShops((p) => [data, ...p]);
      toast.success(`Oficina "${data.name}" criada`);
      setForm(empty);
      setOpen(false);
      setCreatedShop(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (shop) => {
    try {
      const { data } = await api.patch(`/shops/${shop.id}`, { is_active: !shop.is_active });
      setShops((p) => p.map((s) => (s.id === shop.id ? { ...s, ...data } : s)));
      toast.success(data.is_active ? "Oficina ativada" : "Oficina suspensa");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeShop = async (shop) => {
    try {
      await api.delete(`/shops/${shop.id}`);
      setShops((p) => p.filter((s) => s.id !== shop.id));
      toast.success("Oficina removida");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openReset = (shop) => {
    setResetShop(shop);
    setResetPw("");
    setResetConfirm("");
  };
  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetShop) return;
    if (passwordError(resetPw)) { toast.error(passwordError(resetPw)); return; }
    if (resetPw !== resetConfirm) { toast.error("A confirma\u00e7\u00e3o n\u00e3o bate com a nova senha"); return; }
    setResetting(true);
    try {
      const { data } = await api.post(`/shops/${resetShop.id}/reset-owner-password`, { new_password: resetPw });
      toast.success(`Senha do dono de "${resetShop.name}" redefinida.`);
      setResetResult({ shop_name: resetShop.name, owner_email: data?.owner_email || resetShop.owner_email, new_password: resetPw });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResetting(false);
    }
  };

  const closeReset = () => {
    setResetShop(null);
    setResetPw(""); setResetConfirm("");
    setResetResult(null); setCopied(false);
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(resetResult?.new_password || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.info("N\u00e3o foi poss\u00edvel copiar automaticamente");
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <BusinessDashboard />
      <TicketsAdmin />
      <PlansAdmin />
      <AddonsAdmin />
      <HelpAdmin />
      <NoticesAdmin />
      <BillingAdmin />
      <AiUsageAdmin />
      <AssistantUsageAdmin />
      <PlateLookupAdmin />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono-caps text-zinc-500">Super administrador</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">
            Rede de oficinas
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Cadastre, suspenda e acompanhe todas as oficinas da sua rede.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-shop-btn"
              className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase tracking-wide h-11 px-5"
            >
              <Plus className="h-4 w-4 mr-2" /> Nova oficina
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display uppercase">Cadastrar oficina</DialogTitle>
              <DialogDescription className="text-zinc-500">
                Crie a oficina (tenant) e defina o acesso do dono. Ele receberá um e-mail com o link do app, o e-mail cadastrado e a senha inicial.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="new-shop-form">
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Nome da oficina</Label>
                <Input
                  data-testid="shop-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="mt-1 bg-zinc-900 border-zinc-800"
                  placeholder="Oficina do Zé"
                />
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Nome do dono</Label>
                <Input
                  data-testid="shop-owner-name-input"
                  required
                  value={form.owner_name}
                  onChange={(e) => setForm((p) => ({ ...p, owner_name: e.target.value }))}
                  className="mt-1 bg-zinc-900 border-zinc-800"
                  placeholder="José da Silva"
                />
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">E-mail do dono</Label>
                <Input
                  data-testid="shop-owner-email-input"
                  type="email"
                  required
                  value={form.owner_email}
                  onChange={(e) => setForm((p) => ({ ...p, owner_email: e.target.value }))}
                  className="mt-1 bg-zinc-900 border-zinc-800"
                  placeholder="dono@oficina.com"
                />
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Acesso do dono</Label>
                <p data-testid="shop-owner-invite-hint" className="mt-1 text-xs text-zinc-500">O dono recebe por e-mail um link "Criar minha senha" (72h, uso único). Nenhuma senha é enviada.</p>
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">WhatsApp do dono (opcional)</Label>
                <Input
                  data-testid="shop-owner-phone-input"
                  value={form.owner_phone}
                  onChange={(e) => setForm((p) => ({ ...p, owner_phone: e.target.value }))}
                  className="mt-1 bg-zinc-900 border-zinc-800"
                  placeholder="11999998888"
                  inputMode="tel"
                />
              </div>
              <ShopProfileFields form={newProfile} setForm={setNewProfile} />
              <Button
                data-testid="shop-submit-btn"
                type="submit"
                disabled={saving}
                className="w-full h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar oficina"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <ShopCreatedDialog shop={createdShop} onClose={() => setCreatedShop(null)} />
      <WelcomeResultDialog result={ownerInvite} onClose={() => setOwnerInvite(null)} shopName={ownerInvite?.shop_name} title="Convite reenviado" />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-green-brand" />
        </div>
      ) : shops.length === 0 ? (
        <div
          data-testid="shops-empty"
          className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-10 text-center"
        >
          <Building2 className="h-10 w-10 mx-auto text-zinc-700" />
          <p className="mt-3 text-zinc-400 font-medium">Nenhuma oficina cadastrada.</p>
          <p className="text-sm text-zinc-500 mt-1">Clique em "Nova oficina" para começar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shops.map((s) => (
            <div
              key={s.id}
              data-testid={`shop-card-${s.id}`}
              className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5"
            >
              <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display text-xl font-bold text-zinc-100 truncate" translate="no">
                  <span>{s.name}</span>
                </div>
                <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5 truncate" translate="no">
                  <Mail className="h-3 w-3" />
                  <span>{s.owner_email}</span>
                </div>
              </div>
                <StatusPill shop={s} meta={meta[s.id]} />
              </div>
              <LastAccess shop={s} meta={meta[s.id]} />
              {meta[s.id]?.onboarding && <div data-testid={`shop-onboarding-${s.id}`} className={`mt-1 text-[11px] font-bold ${meta[s.id].onboarding.completed ? "text-green-400" : "text-amber-300"}`}>Primeiro acesso: {meta[s.id].onboarding.completed ? "concluído" : `${meta[s.id].onboarding.count} de ${meta[s.id].onboarding.total}`}</div>}
              <ShopAdminActions shop={s} meta={meta[s.id]} onMeta={onMeta} />
              <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-2.5">
                  <div className="text-zinc-500 font-mono-caps text-[10px]">Clientes</div>
                  <div className="text-zinc-100 font-bold text-lg flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-green-brand" />
                    {s.stats?.customers ?? 0}
                  </div>
                </div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-2.5">
                  <div className="text-zinc-500 font-mono-caps text-[10px]">Ordens</div>
                  <div className="text-zinc-100 font-bold text-lg flex items-center gap-1.5">
                    <ClipboardList className="h-3.5 w-3.5 text-amber-brand" />
                    {s.stats?.orders ?? 0}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <Switch
                    data-testid={`shop-toggle-${s.id}`}
                    checked={s.is_active}
                    onCheckedChange={() => toggleActive(s)}
                  />
                  <span className="text-xs text-zinc-400">
                    {s.is_active ? (
                      <span className="inline-flex items-center gap-1"><PauseCircle className="h-3 w-3" /> Suspender</span>
                    ) : (
                      <span className="inline-flex items-center gap-1"><PlayCircle className="h-3 w-3" /> Ativar</span>
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                <Button
                  data-testid={`shop-edit-${s.id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditShop(s)}
                  className="text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800"
                  title="Editar oficina"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  data-testid={`shop-resend-invite-${s.id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => resendOwnerInvite(s)}
                  className="text-green-neon hover:bg-green-brand/10"
                  title="Reenviar convite ao dono"
                >
                  <Mail className="h-3.5 w-3.5" />
                </Button>
                <Button
                  data-testid={`shop-reset-password-${s.id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => openReset(s)}
                  className="text-amber-300 hover:text-amber-200 hover:bg-amber-brand/10"
                  title="Redefinir senha do dono"
                >
                  <KeyRound className="h-3.5 w-3.5" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      data-testid={`shop-delete-${s.id}`}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-950/40"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-zinc-950 border border-zinc-800 text-zinc-100">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Excluir oficina {s.name}?</AlertDialogTitle>
                      <AlertDialogDescription className="text-zinc-400">
                        Isso remove TODOS os clientes, ordens, orçamentos e fotos desta oficina. Ação irreversível.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-zinc-900 border-zinc-800">Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid={`shop-delete-confirm-${s.id}`}
                        onClick={() => removeShop(s)}
                        className="bg-red-700 hover:bg-red-600 text-white"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <EditShopDialog
        shop={editShop}
        meta={editShop ? meta[editShop.id] : null}
        onMeta={onMeta}
        open={!!editShop}
        onOpenChange={(o) => { if (!o) setEditShop(null); }}
        onSaved={(data) => setShops((p) => p.map((s) => (s.id === data.id ? { ...s, ...data } : s)))}
      />

      <Dialog open={!!resetShop} onOpenChange={(o) => { if (!o) closeReset(); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-amber-brand" /> Redefinir senha do dono
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {resetResult
                ? "Senha redefinida! Guarde a senha abaixo. O dono tamb\u00e9m recebeu um e-mail com estes dados."
                : (resetShop ? `Defina uma nova senha para o dono de "${resetShop.name}" (${resetShop.owner_email}). Ele receber\u00e1 um e-mail com os novos dados.` : "")}
            </DialogDescription>
          </DialogHeader>

          {resetResult ? (
            <div className="space-y-3" data-testid="reset-result-panel">
              <div className="rounded-xl border border-amber-brand/40 bg-amber-brand/5 p-4 space-y-3">
                <div>
                  <div className="text-[11px] font-mono-caps text-amber-200">Oficina</div>
                  <div className="font-display text-base font-bold text-zinc-100" data-testid="reset-result-shop">{resetResult.shop_name}</div>
                </div>
                <div>
                  <div className="text-[11px] font-mono-caps text-amber-200">E-mail (login)</div>
                  <div className="text-sm text-zinc-100 break-all" data-testid="reset-result-email">{resetResult.owner_email}</div>
                </div>
                <div>
                  <div className="text-[11px] font-mono-caps text-amber-200">Nova senha</div>
                  <div className="mt-1 flex items-center gap-2">
                    <code
                      data-testid="reset-result-password"
                      className="flex-1 bg-zinc-950 border border-amber-brand/60 text-amber-brand rounded px-3 py-2 font-mono normal-case font-bold tracking-wider break-all"
                    >
                      {resetResult.new_password}
                    </code>
                    <Button
                      type="button"
                      data-testid="reset-result-copy-btn"
                      onClick={copyPassword}
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800"
                      title="Copiar senha"
                      aria-label="Copiar senha"
                    >
                      {copied ? <Check className="h-4 w-4 text-green-brand" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div className="text-xs text-amber-100/80 flex items-center gap-1.5 pt-1 border-t border-amber-brand/20">
                  <Mail className="h-3.5 w-3.5" /> E-mail enviado para o dono com os dados de acesso.
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button data-testid="reset-result-close" type="button" onClick={closeReset} className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
                  Entendi
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submitReset} className="space-y-3" data-testid="reset-owner-form">
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Nova senha</Label>
                <Input
                  data-testid="reset-owner-password-input"
                  required
                  minLength={8}
                  type="text"
                  value={resetPw}
                  onChange={(e) => setResetPw(e.target.value)}
                  className="mt-1 bg-zinc-900 border-zinc-800"
                  placeholder={PASSWORD_HINT}
                />
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Confirmar nova senha</Label>
                <Input
                  data-testid="reset-owner-password-confirm"
                  required
                  minLength={8}
                  type="text"
                  value={resetConfirm}
                  onChange={(e) => setResetConfirm(e.target.value)}
                  className="mt-1 bg-zinc-900 border-zinc-800"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="ghost" disabled={resetting} onClick={closeReset} className="text-zinc-300 hover:bg-zinc-900">
                  Cancelar
                </Button>
                <Button
                  data-testid="reset-owner-submit-btn"
                  type="submit"
                  disabled={resetting}
                  className="bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase"
                >
                  {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redefinir senha"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShopCreatedDialog({ shop, onClose }) {
  if (!shop) return null;
  const slug = shop.slug;
  const base = window.location.origin;
  const url = slug ? `${base}/o/${slug}` : `${base}/login`;
  const text = inviteText(shop.owner_name, shop.invite_link, shop.name);
  const digits = (shop.owner_phone || "").replace(/\D/g, "");
  const wa = digits ? `https://wa.me/${digits.length <= 11 ? `55${digits}` : digits}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
  const copy = async () => { try { await navigator.clipboard.writeText(shop.invite_link || url); toast.success("Link copiado"); } catch { toast.error("Não foi possível copiar"); } };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="shop-created-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display uppercase flex items-center gap-2">
            {shop.email_sent ? (<><Check className="h-5 w-5 text-green-neon" /> Oficina criada</>) : (<><Mail className="h-5 w-5 text-amber-brand" /> E-mail não enviado</>)}
          </DialogTitle>
          <DialogDescription data-testid="shop-created-message" className={shop.email_sent ? "text-zinc-400" : "text-amber-200"}>
            {shop.email_sent ? "Enviamos ao dono o link \"Criar minha senha\" (72h, uso único). Se quiser, mande também pelo WhatsApp." : "Não foi possível enviar o e-mail agora — copie o link abaixo e envie pelo WhatsApp."}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4 space-y-1 text-sm">
          <div><span className="text-zinc-500">Oficina: </span><span className="font-semibold">{shop.name}</span></div>
          <div><span className="text-zinc-500">Link: </span><span data-testid="shop-created-url" className="break-all">{url}</span></div>
          <div><span className="text-zinc-500">Login: </span><span data-testid="shop-created-email" className="break-all">{shop.owner_email}</span></div>
          {shop.invite_link ? <div><span className="text-zinc-500">Criar senha: </span><span data-testid="shop-created-invite-link" className="break-all text-zinc-300">{shop.invite_link}</span></div> : <div className="text-zinc-500">Dono já tinha conta: ele entra com a senha atual.</div>}
          {shop.owner_phone && (<div><span className="text-zinc-500">WhatsApp: </span><span data-testid="shop-created-phone" className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {shop.owner_phone}</span></div>)}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button data-testid="shop-created-copy" type="button" variant="outline" onClick={copy} className="h-11 bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800 font-bold uppercase"><Copy className="h-4 w-4 mr-2" /> Copiar link</Button>
          <a data-testid="shop-created-whatsapp" href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-11 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold uppercase"><MessageCircle className="h-4 w-4 mr-2" /> WhatsApp</a>
        </div>
        <Button data-testid="shop-created-close" type="button" onClick={onClose} className="h-10 bg-green-brand hover:bg-green-brandDark font-bold uppercase">Fechar</Button>
      </DialogContent>
    </Dialog>
  );
}
